import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { civilisationApi, type CivilisationDoctrine, type CivilisationTaskType } from '../api/civilisation';
import { useToastStore } from '../stores/toastStore';
import { soundEngine } from '../lib/soundEngine';

export function useCivilisation(enabled = true) {
  return useQuery({
    queryKey: ['civilisation'],
    queryFn: () => civilisationApi.getCivilisation(),
    enabled,
    staleTime: 3000,
    refetchInterval: 10000,
  });
}

function useCivilisationInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['civilisation'] });
    queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
    queryClient.invalidateQueries({ queryKey: ['development', 'projects'] });
  };
}

export function useStartCivilisationTask() {
  const invalidate = useCivilisationInvalidation();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (taskType: CivilisationTaskType) => civilisationApi.startTask(taskType),
    onSuccess: () => {
      soundEngine.playPurchaseChime();
      invalidate();
      addToast('Civilisation task started.', 'success');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to start civilisation task';
      addToast(msg, 'error');
    },
  });
}

export function useNameEmpire() {
  const invalidate = useCivilisationInvalidation();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (name: string) => civilisationApi.nameEmpire(name),
    onSuccess: () => {
      soundEngine.playPurchaseChime();
      invalidate();
      addToast('Empire name locked in.', 'success');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to name empire';
      addToast(msg, 'error');
    },
  });
}

export function useSetCivilisationDoctrine() {
  const invalidate = useCivilisationInvalidation();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (doctrine: CivilisationDoctrine) => civilisationApi.setDoctrine(doctrine),
    onSuccess: () => {
      soundEngine.playPurchaseChime();
      invalidate();
      addToast('Civilisation doctrine set.', 'success');
    },
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Failed to set doctrine';
      addToast(msg, 'error');
    },
  });
}

export function useAmbassadorDialogue() {
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: (promptId: string) => civilisationApi.dialogue(promptId),
    onError: (error: any) => {
      soundEngine.playErrorBuzz();
      const msg = error.response?.data?.error?.message || 'Ambassador is not ready to answer';
      addToast(msg, 'error');
    },
  });
}
